import React, { useState } from "react";
import {
  Form,
  Button,
  Alert,
  Col,
  Row,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import {
  FaPhone,
  FaGraduationCap,
  FaIdBadge,
  FaUniversity,
  FaUserShield,
  FaEyeSlash,
  FaEye,
} from "react-icons/fa";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../api";

const SignUp = () => {
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    password: "",
    confirmPassword: "",
    role: "student", // Default role
    email: "", // Added for admin/staff
    organizationName: "",
    studentNumber: "",
    contactNumber: "",
    degree: "",
    faculty: "",
    volunteeringInterests: [],
    consent: false,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isBtnHovered, setIsBtnHovered] = useState(false);
  const navigate = useNavigate();

  const faculties = [
    "Commerce, Law and Management",
    "Health Sciences",
    "Engineering and the Built Environment",
    "Humanities",
    "Science",
  ];

  const interests = [
    "Wits Food Bank",
    "Masidleni Daily Meals",
    "Wits Food Gardens",
    "Wits Student Communal Kitchen",
    "Community Food Programmes",
    "Developing Food Gardens",
    "Urban Waste Management",
    "Reading Clubs",
    "In-person tutoring for high school and primary school learners",
    "Online tutoring for high school and primary school learners",
    "After school assistance programmes",
    "Health and allied assessments",
    "Sports development",
    "Arts and Cultural Development Programmes",
    "Animal welfare",
    "Befriending the elderly",
    "Clothing drive",
    "Hygiene drive",
    "Food drives",
    "Social Justice Campaigns",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError(""); // Reset error
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleInterestsChange = (e) => {
    const { value, checked } = e.target;
    setError(""); // Reset error
    setFormData((prevState) => {
      let newInterests;
      if (checked) {
        if (prevState.volunteeringInterests.length >= 3) {
          setError("You can select up to 3 interests");
          return prevState;
        } else {
          newInterests = [...prevState.volunteeringInterests, value];
        }
      } else {
        newInterests = prevState.volunteeringInterests.filter(
          (interest) => interest !== value
        );
      }
      return {
        ...prevState,
        volunteeringInterests: newInterests,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Password confirmation
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    // Password strength validation
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      setError(
        "Password must be at least 8 characters long and include uppercase, lowercase letters, a number, and a special character."
      );
      setLoading(false);
      return;
    }

    // Consent validation
    if (!formData.consent) {
      setError("You must agree to the processing of your personal information");
      setLoading(false);
      return;
    }

    // Student-specific validations
    if (formData.role === "student") {
      // Interest validation
      if (formData.volunteeringInterests.length !== 3) {
        setError("Please select exactly 3 volunteering interests");
        setLoading(false);
        return;
      }

      // Student number validation
      if (!/^\d{6,7}$/.test(formData.studentNumber)) {
        setError("Student number should contain 6 or 7 digits");
        setLoading(false);
        return;
      }
    }

    if (formData.role === "organization" && !formData.organizationName.trim()) {
      setError("Organization name is required");
      setLoading(false);
      return;
    }

    try {
      const email =
        formData.role === "student"
          ? `${formData.studentNumber}@students.wits.ac.za`
          : formData.email; // For admin, email is entered manually

      const dataToSend = {
        name: formData.name,
        surname: formData.surname,
        email: email,
        password: formData.password,
        role: formData.role,
      };

      if (formData.role === "student") {
        dataToSend.studentNumber = formData.studentNumber;
        dataToSend.contactNumber = formData.contactNumber;
        dataToSend.degree = formData.degree;
        dataToSend.faculty = formData.faculty;
        dataToSend.volunteeringInterests = formData.volunteeringInterests;
      } else if (formData.role === "organization") {
        dataToSend.organizationName = formData.organizationName.trim();
      }

      const response = await axios.post(
        `${API_BASE_URL}/auth/signup`,
        dataToSend
      );

      if (response.status === 201) {
        console.log("Signup successful:", response.data);
        setError(""); // Clear any existing errors
        alert(
          `Signup successful! Please check your email (${email}) to verify your account.`
        );
        navigate("/signin");
      } else {
        setError(response.data.message || "Something went wrong");
      }
    } catch (err) {
      console.error("Error during signup:", err);
      setError(err.response?.data?.error || "Error during signup");
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="auth-page-container">
      {/* ── Left Visual Panel (Desktop) ── */}
      <div 
        className="d-none d-lg-flex flex-column justify-content-between p-5 text-white"
        style={{
          width: "40%",
          background: "linear-gradient(135deg, var(--color-primary-dark) 0%, var(--color-primary) 100%)",
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* Background glow elements */}
        <div style={{
          position: "absolute",
          top: "-10%",
          right: "-10%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(42, 157, 143, 0.18) 0%, transparent 70%)",
          pointerEvents: "none"
        }} />
        <div style={{
          position: "absolute",
          bottom: "-10%",
          left: "-10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(181, 131, 42, 0.12) 0%, transparent 70%)",
          pointerEvents: "none"
        }} />

        {/* Logo Badge */}
        <div style={{
          background: "#FFFFFF",
          padding: "0.6rem 1.2rem",
          borderRadius: "var(--radius-md)",
          display: "inline-block",
          alignSelf: "flex-start",
          boxShadow: "var(--shadow-sm)",
          zIndex: 2
        }}>
          <img
            src="newlogo1.jpg"
            alt="CSD Logo"
            style={{ maxHeight: "48px", width: "auto", objectFit: "contain" }}
          />
        </div>

        {/* Main Brand Content */}
        <div className="my-auto text-start" style={{ zIndex: 2 }}>
          <h1 style={{ fontSize: "2.5rem", fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.5px", marginBottom: "1.5rem" }}>
            Join WCCO Outreach
          </h1>
          <p style={{ fontSize: "1.05rem", color: "rgba(255, 255, 255, 0.85)", lineHeight: 1.6, maxWidth: "400px", marginBottom: "2rem" }}>
            Create your account to start volunteering, log your hours, and develop key civic leadership skills with CSD.
          </p>

          {/* Visual Divider Accent */}
          <div style={{ width: "60px", height: "4px", backgroundColor: "var(--color-teal)", borderRadius: "2px", marginBottom: "2.5rem" }} />

          {/* Stat counters */}
          <div className="d-flex gap-4">
            <div>
              <div style={{ fontSize: "1.6rem", fontWeight: 800, color: "var(--color-teal-light)" }}>5000+</div>
              <div style={{ fontSize: "0.78rem", color: "rgba(255, 255, 255, 0.7)", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>Hours Logged</div>
            </div>
            <div style={{ width: "1px", backgroundColor: "rgba(255, 255, 255, 0.2)", alignSelf: "stretch" }} />
            <div>
              <div style={{ fontSize: "1.6rem", fontWeight: 800, color: "var(--color-teal-light)" }}>150+</div>
              <div style={{ fontSize: "0.78rem", color: "rgba(255, 255, 255, 0.7)", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>Active Projects</div>
            </div>
          </div>
        </div>

        {/* Footer info */}
        <div style={{ fontSize: "0.8rem", color: "rgba(255, 255, 255, 0.4)", zIndex: 2 }}>
          © {new Date().getFullYear()} WCCO. All rights reserved.
        </div>
      </div>

      {/* ── Right Form Column ── */}
      <div className="auth-form-column">
        <div 
          className="auth-card"
          style={{
            maxWidth: "600px"
          }}
        >
          {/* Logo (Mobile display only) */}
          <div className="d-lg-none text-center mb-4">
            <img
              src="newlogo1.jpg"
              alt="CSD Logo"
              style={{ maxHeight: "60px", width: "auto", objectFit: "contain", marginBottom: "1rem" }}
            />
          </div>

          <div className="text-start mb-4">
            <h3 style={{ color: "var(--color-primary)", fontWeight: 800, letterSpacing: "-0.5px", marginBottom: "0.4rem" }}>
              Create Account
            </h3>
            <p style={{ color: "var(--color-text-secondary)", fontSize: "0.9rem", margin: 0 }}>
              Join structured civic programs and log your contributions.
            </p>
          </div>

          {error && (
            <Alert 
              variant="danger" 
              style={{ 
                fontSize: "0.85rem", 
                borderRadius: "var(--radius-md)", 
                border: "none",
                backgroundColor: "rgba(193, 57, 43, 0.08)",
                color: "var(--color-red)",
                fontWeight: 500,
                textAlign: "left"
              }}
            >
              {error}
            </Alert>
          )}

          <Form onSubmit={handleSubmit} className="text-start">
            <Row className="g-3">
              {/* Role Selection */}
              <Col xs={12}>
                <Form.Group>
                  <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)", marginBottom: "0.4rem" }}>
                    <FaUserShield className="me-1" /> Choose Role
                  </Form.Label>
                  <Form.Select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    style={{
                      borderRadius: "var(--radius-md)",
                      borderColor: "var(--color-border)",
                      padding: "0.65rem 0.85rem",
                      fontSize: "0.95rem"
                    }}
                  >
                    <option value="student">Student Volunteer</option>
                    <option value="admin">Administrator / Staff</option>
                    <option value="organization">Project Organisation</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Name & Surname */}
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)", marginBottom: "0.4rem" }}>
                    First Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter first name"
                    style={{
                      borderRadius: "var(--radius-md)",
                      borderColor: "var(--color-border)",
                      padding: "0.65rem 0.85rem",
                      fontSize: "0.95rem"
                    }}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)", marginBottom: "0.4rem" }}>
                    Surname
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="surname"
                    value={formData.surname}
                    onChange={handleChange}
                    required
                    placeholder="Enter surname"
                    style={{
                      borderRadius: "var(--radius-md)",
                      borderColor: "var(--color-border)",
                      padding: "0.65rem 0.85rem",
                      fontSize: "0.95rem"
                    }}
                  />
                </Form.Group>
              </Col>

              {formData.role === "organization" && (
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Organisation Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="organizationName"
                      value={formData.organizationName}
                      onChange={handleChange}
                      required
                      placeholder="Enter the registered or public organisation name"
                    />
                  </Form.Group>
                </Col>
              )}

              {/* Student Number or Email */}
              <Col xs={12}>
                {formData.role === "student" ? (
                  <Form.Group>
                    <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)", marginBottom: "0.4rem" }}>
                      <FaIdBadge className="me-1" /> Student Number
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="studentNumber"
                      value={formData.studentNumber}
                      onChange={handleChange}
                      required
                      placeholder="e.g. 1234567"
                      pattern="\d{6,7}"
                      title="Student number should contain 6 or 7 digits"
                      style={{
                        borderRadius: "var(--radius-md)",
                        borderColor: "var(--color-border)",
                        padding: "0.65rem 0.85rem",
                        fontSize: "0.95rem"
                      }}
                    />
                    <Form.Text className="text-muted" style={{ fontSize: "0.78rem" }}>
                      Your email will auto-configure as: <i>student_number@students.wits.ac.za</i>
                    </Form.Text>
                  </Form.Group>
                ) : (
                  <Form.Group>
                    <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)", marginBottom: "0.4rem" }}>
                      <FaIdBadge className="me-1" /> {formData.role === "organization" ? "Organisation Email Address" : "Staff Email Address"}
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={formData.role === "organization" ? "contact@organisation.org" : "name.surname@wits.ac.za"}
                      required
                      pattern={formData.role === "admin" ? "^[a-zA-Z0-9.-]+\\.[a-zA-Z0-9.-]+@wits\\.ac\\.za$" : undefined}
                      title={formData.role === "admin" ? "Staff email should be in the format name.surname@wits.ac.za" : undefined}
                      style={{
                        borderRadius: "var(--radius-md)",
                        borderColor: "var(--color-border)",
                        padding: "0.65rem 0.85rem",
                        fontSize: "0.95rem"
                      }}
                    />
                  </Form.Group>
                )}
              </Col>

              {/* Password */}
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)", marginBottom: "0.4rem" }}>
                    Password
                  </Form.Label>
                  <InputGroup style={{ borderRadius: "var(--radius-md)", overflow: "hidden" }}>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      placeholder="Create password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      style={{
                        borderColor: "var(--color-border)",
                        padding: "0.65rem 0.85rem",
                        fontSize: "0.95rem",
                        borderRight: "none"
                      }}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={togglePasswordVisibility}
                      style={{
                        borderColor: "var(--color-border)",
                        backgroundColor: "transparent",
                        color: "var(--color-text-muted)",
                        padding: "0 0.85rem"
                      }}>
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>

              {/* Confirm Password */}
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)", marginBottom: "0.4rem" }}>
                    Confirm Password
                  </Form.Label>
                  <InputGroup style={{ borderRadius: "var(--radius-md)", overflow: "hidden" }}>
                    <Form.Control
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Re-enter password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      style={{
                        borderColor: "var(--color-border)",
                        padding: "0.65rem 0.85rem",
                        fontSize: "0.95rem",
                        borderRight: "none"
                      }}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{
                        borderColor: "var(--color-border)",
                        backgroundColor: "transparent",
                        color: "var(--color-text-muted)",
                        padding: "0 0.85rem"
                      }}>
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>
              
              <Col xs={12}>
                <div style={{ fontSize: "0.78rem", color: "var(--color-text-secondary)", lineHeight: 1.4, marginTop: "-0.25rem", padding: "0 0.2rem" }}>
                  * Password must be at least 8 characters long and include uppercase, lowercase, a number, and a special character.
                </div>
              </Col>

              {/* Student Specific Fields */}
              {formData.role === "student" && (
                <>
                  {/* Contact Number */}
                  <Col xs={12} sm={6}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)", marginBottom: "0.4rem" }}>
                        <FaPhone className="me-1" /> Contact Number
                      </Form.Label>
                      <Form.Control
                        type="tel"
                        name="contactNumber"
                        value={formData.contactNumber}
                        onChange={handleChange}
                        required
                        placeholder="e.g. 0812345678"
                        style={{
                          borderRadius: "var(--radius-md)",
                          borderColor: "var(--color-border)",
                          padding: "0.65rem 0.85rem",
                          fontSize: "0.95rem"
                        }}
                      />
                    </Form.Group>
                  </Col>

                  {/* Degree */}
                  <Col xs={12} sm={6}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)", marginBottom: "0.4rem" }}>
                        <FaGraduationCap className="me-1" /> Degree Program
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="degree"
                        value={formData.degree}
                        onChange={handleChange}
                        required
                        placeholder="e.g. BSc Computer Science"
                        style={{
                          borderRadius: "var(--radius-md)",
                          borderColor: "var(--color-border)",
                          padding: "0.65rem 0.85rem",
                          fontSize: "0.95rem"
                        }}
                      />
                    </Form.Group>
                  </Col>

                  {/* Faculty */}
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)", marginBottom: "0.4rem" }}>
                        <FaUniversity className="me-1" /> Wits Faculty
                      </Form.Label>
                      <Form.Select
                        name="faculty"
                        value={formData.faculty}
                        onChange={handleChange}
                        required
                        style={{
                          borderRadius: "var(--radius-md)",
                          borderColor: "var(--color-border)",
                          padding: "0.65rem 0.85rem",
                          fontSize: "0.95rem"
                        }}
                      >
                        <option value="">Select Faculty</option>
                        {faculties.map((fac, idx) => (
                          <option key={idx} value={fac}>
                            {fac}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* Volunteering Interests (Choose exactly 3) */}
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)", marginBottom: "0.2rem" }}>
                        Volunteering Areas of Interest
                      </Form.Label>
                      <div style={{ fontSize: "0.8rem", color: "var(--color-text-secondary)", marginBottom: "0.5rem" }}>
                        Please select **exactly 3** programs you wish to join:
                      </div>
                      <div
                        style={{
                          maxHeight: "180px",
                          overflowY: "auto",
                          border: "1px solid var(--color-border)",
                          borderRadius: "var(--radius-md)",
                          padding: "0.8rem",
                          backgroundColor: "var(--color-bg)",
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.4rem"
                        }}
                      >
                        {interests.map((interest, idx) => {
                          const isChecked = formData.volunteeringInterests.includes(interest);
                          return (
                            <Form.Check
                              key={idx}
                              type="checkbox"
                              id={`interest-${idx}`}
                              label={interest}
                              value={interest}
                              checked={isChecked}
                              onChange={handleInterestsChange}
                              disabled={!isChecked && formData.volunteeringInterests.length >= 3}
                              style={{
                                fontSize: "0.88rem",
                                color: isChecked ? "var(--color-primary)" : "var(--color-text)",
                                fontWeight: isChecked ? 600 : 400
                              }}
                            />
                          );
                        })}
                      </div>
                    </Form.Group>
                  </Col>
                </>
              )}

              {/* Consent POPI Act Checkbox */}
              <Col xs={12}>
                <div 
                  style={{
                    backgroundColor: "rgba(42, 157, 143, 0.05)",
                    border: "1px solid rgba(42, 157, 143, 0.15)",
                    borderRadius: "var(--radius-md)",
                    padding: "0.85rem",
                    marginTop: "0.5rem"
                  }}
                >
                  <Form.Check
                    type="checkbox"
                    name="consent"
                    id="popi-consent"
                    checked={formData.consent}
                    onChange={(e) =>
                      setFormData({ ...formData, consent: e.target.checked })
                    }
                    required
                    label={
                      <span style={{ fontSize: "0.85rem", color: "var(--color-text-secondary)", lineHeight: 1.4 }}>
                        I agree to the processing of my personal information in accordance with the <b>POPI Act</b> for outreach coordination purposes (
                        <a
                          href="/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "var(--color-teal)", fontWeight: 600, textDecoration: "none" }}
                        >
                          Privacy Policy
                        </a>).
                      </span>
                    }
                  />
                </div>
              </Col>

              {/* Submit Button */}
              <Col xs={12} className="mt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  onMouseEnter={() => setIsBtnHovered(true)}
                  onMouseLeave={() => setIsBtnHovered(false)}
                  style={{
                    backgroundColor: isBtnHovered ? "var(--color-primary-hover)" : "var(--color-primary)",
                    borderColor: isBtnHovered ? "var(--color-primary-hover)" : "var(--color-primary)",
                    borderRadius: "var(--radius-md)",
                    padding: "0.75rem",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    width: "100%",
                    transition: "all var(--transition)",
                    boxShadow: isBtnHovered ? "var(--shadow-md)" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem"
                  }}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <span>Registering...</span>
                    </>
                  ) : (
                    "Register & Sign Up"
                  )}
                </Button>
              </Col>
            </Row>
          </Form>

          <div className="text-center mt-4" style={{ fontSize: "0.9rem", color: "var(--color-text-secondary)" }}>
            Already have an account?{" "}
            <Link to="/signin" style={{ color: "var(--color-teal)", fontWeight: 600, textDecoration: "none" }}>
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
